import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows } from '@/components/KstDataGrid';
import { AdminTabBar } from '@/components/AdminTabBar';
import { ConfirmModal } from '@/components/ConfirmModal';
import { AddStandardViolationTypeModal, type ViolationTypeOption } from '@/components/modals/AddStandardViolationTypeModal';
import { AddCustomViolationTypeModal } from '@/components/modals/AddCustomViolationTypeModal';
import { Dropdownlist } from '@/components/Dropdownlist';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { currentUserHasPermission, hasValue } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';

// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const deepCopyProperties = _wg('deepCopyProperties');

const VIOLATION_TYPE_OTHER = 16;

function sortViolationTypeList(list: any[]) {
  list.sort((a, b) => {
    if (a.listIdx === null && b.listIdx === null) return 0;
    if (a.listIdx === null) return 1;
    if (b.listIdx === null) return -1;
    return a.listIdx - b.listIdx;
  });
}

const tabs = [
  { id: 'showSettingsTab', label: 'Settings' },
  { id: 'showViolationTypeManagementTab', label: 'Violations' },
];

const violationTypeColumns: KstDataGridColumn[] = [
  { field: 'code', name: 'Code', size: 'hidden' },
  {
    field: 'value',
    name: 'Violation Type',
    size: 'col-xs-8',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.value)) {
        return e.value;
      }
      return 'Error getting Violation Type.';
    },
  },
  {
    field: 'isGlobal',
    name: 'Type',
    size: 'col-xs-2',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.isGlobal)) {
        if (e.isGlobal === true) {
          return '<span style="color: #0066cc; font-weight: 500;">Standard</span>';
        } else {
          return '<span style="color: #28a745; font-weight: 500;">Custom</span>';
        }
      }
      return '<span style="color: #999;">Unknown</span>';
    },
  },
];

const violationTypeHeaderButtons = [
  { name: 'Add Standard', function: 'addstandard', src: '/Content/TourImages/add-r.svg' },
  { name: 'Add Custom', function: 'addcustom', src: '/Content/TourImages/add-r.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

export function CitationManagementScreen() {
  const { theme } = useTheme();

  const [hasAccess, setHasAccess] = useState(true);
  const [activeTab, setActiveTab] = useState('showSettingsTab');
  const [activePage, setActivePage] = useState('showSettings');

  // Citation settings
  const [isCourtDateEnabled, setIsCourtDateEnabled] = useState<boolean | null>(null);
  const [isPublicNotesEnabled, setIsPublicNotesEnabled] = useState<boolean | null>(null);

  const [offenderMode, setOffenderMode] = useState<number>(0);

  // Violation type management
  const [violationTypeEntries, setViolationTypeEntries] = useState<any[]>([]);
  const violationTypeListCacheRef = useRef<any[] | null>(null);
  const globalViolationTypeListCacheRef = useRef<any[] | null>(null);

  // Add Standard Violation Type modal
  const [addStandardModalOpen, setAddStandardModalOpen] = useState(false);
  const [addStandardLoading, setAddStandardLoading] = useState(false);
  const [availableStandardViolationTypes, setAvailableStandardViolationTypes] = useState<ViolationTypeOption[]>([]);

  // Add Custom Violation Type modal
  const [addCustomModalOpen, setAddCustomModalOpen] = useState(false);

  // Delete confirm modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmItems, setDeleteConfirmItems] = useState<any[]>([]);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');

  // Loading state
  const [isViolationsLoading, setIsViolationsLoading] = useState<boolean>(false);

  // Grid ref for scroll
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const violationTypeRows: KstDataGridRows = useMemo(
    () => ({ entries: violationTypeEntries }),
    [violationTypeEntries]
  );

  // Check access on mount
  useEffect(() => {
    if (typeof currentUserHasPermission === 'function' && typeof AccessAuthorities !== 'undefined') {
      if (!currentUserHasPermission(AccessAuthorities.ROLE_CITATION_MANAGEMENT)) {
        setHasAccess(false);
        return;
      }
    }
    // Load citation settings
    refreshCitationSettings();
  }, []);

  const refreshCitationSettings = useCallback(() => {
    api.getCitationSettings(
      (result: any) => {
        setIsCourtDateEnabled(result.isCourtDateEnabled);
        setIsPublicNotesEnabled(result.isPublicNotesEnabled ?? false);
        setOffenderMode(result.offenderMode ?? 0);
      },
      () => {
        App.showPopupNotification('Error getting citation settings.', 'error');
      }
    );
  }, []);

  const getGlobalViolationTypeList = useCallback((): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      if (hasValue(globalViolationTypeListCacheRef.current)) {
        resolve(deepCopyProperties(globalViolationTypeListCacheRef.current));
      } else {
        api.getGlobalViolationTypes(
          (result: any) => {
            globalViolationTypeListCacheRef.current = result;
            resolve(deepCopyProperties(result));
          },
          (errorResult: any) => {
            reject(errorResult);
            App.showPopupNotification('Error getting standard violation types.', 'error');
          }
        );
      }
    });
  }, []);

  const getCompanyViolationTypeList = useCallback((): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      if (hasValue(violationTypeListCacheRef.current)) {
        resolve(deepCopyProperties(violationTypeListCacheRef.current));
      } else {
        api.getCompanyViolationTypes(
          (result: any) => {
            violationTypeListCacheRef.current = result;
            resolve(deepCopyProperties(result));
          },
          (errorResult: any) => {
            reject(errorResult);
            App.showPopupNotification('Error getting violation types.', 'error');
          }
        );
      }
    });
  }, []);

  const refreshManageViolationTypeList = useCallback(
    (doScrollToBottom = false) => {
      setIsViolationsLoading(true);
      getCompanyViolationTypeList().then((value: any[]) => {
        const filtered = value.filter((item: any) => item.code !== VIOLATION_TYPE_OTHER);
        sortViolationTypeList(filtered);
        for (let i = 0; i < filtered.length; i++) {
          filtered[i].listIdx = i + 1;
          filtered[i].isListEnabled = true;
        }
        setViolationTypeEntries(filtered);
        setIsViolationsLoading(false);

        if (doScrollToBottom) {
          setTimeout(() => {
            const scrollEl = gridContainerRef.current?.closest('.kst-main-content');
            if (scrollEl) {
              scrollEl.scrollTop = scrollEl.scrollHeight;
            }
          }, 0);
        }
      }).catch(() => {
        setIsViolationsLoading(false);
      });
    },
    [getCompanyViolationTypeList]
  );

  const updateViolationTypes = useCallback(
    (entries: any[], doScrollToBottom = false, skipRefresh = false) => {
      const violationTypes = entries.map((elt: any) => ({
        code: elt.code,
        listIdx: elt.listIdx,
      }));

      // Ensure "Other" (code 16) is always included
      const hasOther = violationTypes.some((vt: any) => vt.code === VIOLATION_TYPE_OTHER);
      if (!hasOther) {
        const maxListIdx =
          violationTypes.length > 0
            ? Math.max(...violationTypes.map((vt: any) => vt.listIdx))
            : -1;
        violationTypes.push({ code: VIOLATION_TYPE_OTHER, listIdx: maxListIdx + 1 });
      }

      api.updateViolationTypeList(
        violationTypes,
        () => {
          violationTypeListCacheRef.current = null;
          if (!skipRefresh) {
            refreshManageViolationTypeList(doScrollToBottom);
          }
        },
        (failResult: any) => {
          let errorMessage = 'Failed to update violation types.';

          if (failResult?.errors?._embedded?.errors) {
            const errors = failResult.errors._embedded.errors;
            if (errors.length > 0) {
              const errorCode = errors[0].errorCode;
              switch (errorCode) {
                case 'violationTypeList.nullable':
                  errorMessage = 'Violation type list cannot be empty.';
                  break;
                case 'violationTypeList.nullableElement':
                  errorMessage = 'Invalid violation type entry detected.';
                  break;
                case 'violationTypeList.nullableListIdx':
                  errorMessage = 'Violation type list index cannot be null.';
                  break;
                case 'violationTypeList.duplicateElement':
                  errorMessage = 'Duplicate violation types detected in the list.';
                  break;
                case 'violationTypeList.invalid':
                  errorMessage = 'Invalid violation type code detected.';
                  break;
                case 'violationType.other.cannotDelete':
                case 'com.kerkton.orna.violationType.other.cannotDelete':
                  errorMessage =
                    'Violation type "Other" (code 16) cannot be removed - it is required for all companies.';
                  break;
                default:
                  if (errors[0].message) {
                    errorMessage = errors[0].message;
                  }
                  break;
              }
            }
          }

          App.showPopupNotification(errorMessage, 'error');
        }
      );
    },
    [refreshManageViolationTypeList]
  );

  const handleTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      if (tabId === 'showSettingsTab') {
        setActivePage('showSettings');
      } else if (tabId === 'showViolationTypeManagementTab') {
        setActivePage('showViolationTypeManagement');
        refreshManageViolationTypeList();
      }
    },
    [refreshManageViolationTypeList]
  );

  const handleUpdateCitationSettings = useCallback(() => {
    api.updateCitationSettings(
      { isCourtDateEnabled, isPublicNotesEnabled, offenderMode },
      () => {
        App.showPopupNotification('Citation settings saved.', 'success');
      },
      () => {
        App.showPopupNotification('Error updating citation settings.', 'error');
      }
    );
  }, [isCourtDateEnabled, isPublicNotesEnabled, offenderMode]);

  const handleButtonCommand = useCallback(
    (functionName: string, checkedItems: any[]) => {
      if (functionName === 'addstandard') {
        setAddStandardModalOpen(true);
        setAddStandardLoading(true);
        Promise.all([getCompanyViolationTypeList(), getGlobalViolationTypeList()]).then(
          ([companyTypes, globalTypes]) => {
            const codeMap: Record<string, boolean> = {};
            companyTypes.forEach((item: any) => {
              codeMap[item.code] = true;
            });
            const available = globalTypes.filter(
              (vt: any) => !codeMap[vt.code.toString()] && vt.code !== VIOLATION_TYPE_OTHER
            );
            setAvailableStandardViolationTypes(available);
            setAddStandardLoading(false);
          }
        ).catch(() => {
          setAddStandardLoading(false);
        });
      } else if (functionName === 'addcustom') {
        setAddCustomModalOpen(true);
      } else if (functionName === 'delete') {
        if (checkedItems.length === 0) {
          App.showPopupNotification('Violation type(s) must be selected to delete.', 'warning');
          return;
        }
        const hasOther = checkedItems.some((item: any) => item.code === VIOLATION_TYPE_OTHER);
        if (hasOther) {
          App.showPopupNotification(
            'Violation type "Other" (code 16) cannot be deleted - it is required for all companies.',
            'error'
          );
          return;
        }
        const count = checkedItems.length;
        const message =
          count === 1
            ? `Are you sure you want to delete "${checkedItems[0].value}"?`
            : `Are you sure you want to delete ${count} violation types?`;
        setDeleteConfirmItems(checkedItems);
        setDeleteConfirmMessage(message);
        setDeleteConfirmOpen(true);
      }
    },
    [getCompanyViolationTypeList, getGlobalViolationTypeList]
  );

  const handleAddStandardSave = useCallback(
    (data: { violationTypeCodes: number[] }) => {
      const updated = [...violationTypeEntries];
      for (const code of data.violationTypeCodes) {
        updated.push({ code, listIdx: null });
      }
      // Renumber sequentially
      for (let i = 0; i < updated.length; i++) {
        updated[i].listIdx = i + 1;
      }
      setIsViolationsLoading(true);
      updateViolationTypes(updated, true);
      setAddStandardModalOpen(false);
      setAddStandardLoading(false);
    },
    [violationTypeEntries, updateViolationTypes]
  );

  const handleAddCustomSave = useCallback(
    (data: { violationTypeValue: string }) => {
      api.addCompanyViolationType(
        data.violationTypeValue,
        () => {
          violationTypeListCacheRef.current = null;
          refreshManageViolationTypeList(true);
          setAddCustomModalOpen(false);
        },
        (failResult: any) => {
          let errorMessage = 'Failed to save violation type.';
          if (failResult?.responseJSON) {
            const errorCode = failResult.responseJSON.errorCode || failResult.responseJSON.code;
            if (errorCode === 'nullable' || errorCode === 'notBlank') {
              errorMessage = 'Violation type value is required.';
            } else if (errorCode === 'size' || errorCode === 'maxLength') {
              errorMessage = 'Violation type cannot exceed 255 characters.';
            } else if (failResult.responseJSON.message) {
              errorMessage = failResult.responseJSON.message;
            }
          }
          App.showPopupNotification(errorMessage, 'error');
        }
      );
    },
    [refreshManageViolationTypeList]
  );

  const handleConfirmDelete = useCallback(() => {
    const codeMap: Record<string, boolean> = {};
    deleteConfirmItems.forEach((item: any) => {
      codeMap[item.code] = true;
    });
    const remaining = violationTypeEntries.filter(
      (entry: any) => !codeMap[entry.code.toString()]
    );
    // Renumber sequentially
    for (let i = 0; i < remaining.length; i++) {
      remaining[i].listIdx = i + 1;
    }
    setViolationTypeEntries(remaining);
    updateViolationTypes(remaining);
    setDeleteConfirmOpen(false);
    setDeleteConfirmItems([]);
    setDeleteConfirmMessage('');
  }, [deleteConfirmItems, violationTypeEntries, updateViolationTypes]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDeleteConfirmItems([]);
    setDeleteConfirmMessage('');
  }, []);

  const handleRowMoved = useCallback(
    (row: any) => {
      const values = [...violationTypeEntries];
      const idx = values.findIndex((v: any) => v.code === row.code);
      if (idx >= 0) {
        values.splice(idx, 1);
      }
      // listIdx is 1-based
      values.splice(row.listIdx - 1, 0, row);
      for (let i = 0; i < values.length; i++) {
        values[i].listIdx = i + 1;
      }
      setViolationTypeEntries(values);
      updateViolationTypes(values, false, true);
    },
    [violationTypeEntries, updateViolationTypes]
  );

  if (!hasAccess) {
    return (
      <div className={theme}>
        <div className="kst-page-container">
          <div className="kst-main-content" style={{ paddingTop: '20px' }}>
            <div className="kst-access-denied">
              <h3 className="kst-access-denied-title">Access Denied</h3>
              <p className="kst-access-denied-text">
                You do not have permission to access Citation Management.
              </p>
              <p className="kst-access-denied-help">
                Please contact your administrator if you believe you should have access to this section.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={theme}>
      {/* Modals */}
      <AddStandardViolationTypeModal
        open={addStandardModalOpen}
        availableViolationTypes={availableStandardViolationTypes}
        loading={addStandardLoading}
        theme={theme}
        onSave={handleAddStandardSave}
        onCancel={() => setAddStandardModalOpen(false)}
      />

      <AddCustomViolationTypeModal
        open={addCustomModalOpen}
        theme={theme}
        onSave={handleAddCustomSave}
        onCancel={() => setAddCustomModalOpen(false)}
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete Violation Type"
        message={deleteConfirmMessage}
        confirmText="Delete"
        theme={theme}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <div className="kst-page-container">
        {/* Mobile Tab Navigation */}
        <div className="kst-mobile-nav">
          <AdminTabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className="kst-layout">
          {/* Desktop Sidebar Navigation */}
          <div className="kst-sidebar">
            <ul className="kst-sidebar-list">
              <li
                className={`kst-sidebar-item${activeTab === 'showSettingsTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showSettingsTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="kst-sidebar-text">Settings</span>
              </li>
              <li
                className={`kst-sidebar-item${activeTab === 'showViolationTypeManagementTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showViolationTypeManagementTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 2C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2H6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 13H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M8 17H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span className="kst-sidebar-text">Violations</span>
              </li>
            </ul>
          </div>

          {/* Main Content Area */}
          <div className="kst-main-content">
            {/* Citation Settings Page */}
            {activePage === 'showSettings' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Citation Settings</h3>
                </div>

                {isCourtDateEnabled !== null ? (
                  <div className="kst-settings-form">
                    <div className="kst-settings-section">
                      <div className="kst-checkbox-group">
                        <input
                          type="checkbox"
                          id="chkIsCourtDateEnabled"
                          className="kstc-checkbox"
                          checked={isCourtDateEnabled === true}
                          onChange={(e) => setIsCourtDateEnabled(e.target.checked)}
                        />
                        <label className="kstc-checkbox-label" htmlFor="chkIsCourtDateEnabled">
                          Enable Court Date/Time Field on Citations
                        </label>
                      </div>
                      <p className="kst-checkbox-description">
                        When enabled, officers can specify a court date and time on citations.
                      </p>
                    </div>
                    <div className="kst-settings-section">
                      <div className="kst-checkbox-group">
                        <input
                          type="checkbox"
                          id="chkIsPublicNotesEnabled"
                          className="kstc-checkbox"
                          checked={isPublicNotesEnabled === true}
                          onChange={(e) => setIsPublicNotesEnabled(e.target.checked)}
                        />
                        <label className="kstc-checkbox-label" htmlFor="chkIsPublicNotesEnabled">
                          Enable Public Notes on Citations
                        </label>
                      </div>
                      <p className="kst-checkbox-description">
                        When enabled, officers can add public notes that will be shown on the citation PDF.
                      </p>
                    </div>
                    <div className="kst-settings-section">
                      <div className="kst-checkbox-group">
                        <label className="kstc-checkbox-label">
                          Offender Mode
                        </label>
                        <Dropdownlist
                          value={offenderMode}
                          items={[
                            { label: 'Required', value: 0 },
                            { label: 'Optional', value: 1 },
                            { label: 'Disabled', value: 2 },
                          ]}
                          labelKey="label"
                          valueKey="value"
                          sortItems={false}
                          searchable={false}
                          onChange={(val: number) => setOffenderMode(val)}
                        />
                      </div>
                      <p className="kst-checkbox-description">
                        Required: An offender must be selected on every citation.<br />
                        Optional: Citations can be saved and issued without selecting an offender.<br />
                        Disabled: The offender section is hidden, and offenders cannot be added to citations.
                      </p>
                    </div>
                    <div className="kst-settings-actions">
                      <button
                        type="button"
                        className="kstc-btn kstc-btn--primary"
                        onClick={handleUpdateCitationSettings}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="kst-loading"></div>
                )}
              </div>
            )}

            {/* Violation Type Management Page */}
            {activePage === 'showViolationTypeManagement' && (
              <div className="kst-page" ref={gridContainerRef}>
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Violation Types</h3>
                  <p className="kst-page-description">
                    Update Violation Types displayed on Citations.<br />
                    You will need to log out and in to see the changes on Citations.
                  </p>
                </div>

                <KstDataGrid
                  columns={violationTypeColumns}
                  rows={violationTypeRows}
                  headerbuttons={violationTypeHeaderButtons}
                  showcheckbox={true}
                  showordercolumn={true}
                  mobiletitlefield="value"
                  loading={isViolationsLoading}
                  onButtonCommand={handleButtonCommand}
                  onRowMoved={handleRowMoved}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CitationManagementScreen;

